import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../../lib/motion';
import { AccentInjector } from '../../components/AccentInjector';
import { parseApiError } from '../../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// GATE UI kit — the shared pieces of the gate flow's visual language so every
// gate screen (root, build-clan, join-comp, …) is pixel-identical: unified grey
// ground, GG Anvil lockup, tracked header, white pills, quiet BACK.
// ─────────────────────────────────────────────────────────────────────────────

/** GATE ground — the app-wide unified grey. */
export const GROUND = 'var(--bg-surface)';

export function GateShell({
  header,
  children,
  onBack,
}: {
  header: string;
  children: ReactNode;
  onBack?: () => void;
}) {
  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center px-6 py-12"
      style={{ backgroundColor: GROUND }}
    >
      {/* Gate screens render outside Layout — pull the user's Discord accent
          in here so deeper gate screens don't fall back to forge orange
          (the root GATE's injector cleans up its CSS vars on unmount). */}
      <AccentInjector />
      <div className="w-full max-w-[360px]">
        <img
          src="/brand/gganvil-tall.svg"
          alt="GG Anvil"
          className={cn('mx-auto mb-7 h-28 w-auto', SETTLE)}
          style={settleDelay(0)}
        />
        <p
          className={cn('text-center font-display text-sm font-semibold uppercase tracking-[0.32em] text-white/55', SETTLE)}
          style={settleDelay(1)}
        >
          {header}
        </p>
        <div className="mt-7 flex flex-col gap-4">{children}</div>
      </div>
      {onBack && (
        <button
          onClick={onBack}
          className={cn('absolute bottom-8 left-8 font-display text-lg font-semibold uppercase tracking-wide text-white/85 hover:text-white', SETTLE, MORPH)}
          style={settleDelay(0)}
        >
          Back
        </button>
      )}
    </div>
  );
}

export function GatePill({
  label,
  onClick,
  primary = false,
  idx,
  disabled = false,
  caption,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  idx: number;
  disabled?: boolean;
  caption?: string;
}) {
  const style: CSSProperties = primary
    ? { ...settleDelay(idx), backgroundColor: 'var(--accent-500)' }
    : settleDelay(idx);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={style}
      className={cn(
        SETTLE,
        MORPH,
        'w-full rounded-full px-8 py-4 text-center font-display text-[15px] font-semibold uppercase tracking-wide shadow-[0_3px_14px_rgba(0,0,0,0.22)] disabled:opacity-50',
        primary ? 'text-[#1a1206]' : 'bg-white text-[#1f1f23] hover:bg-white/90',
      )}
    >
      {label}
      {caption && (
        <span className="mt-0.5 block font-sans text-[10px] font-medium normal-case tracking-normal opacity-60">
          {caption}
        </span>
      )}
    </button>
  );
}

/** Text input in the gate language — solid white rounded field, no border. */
export function GateInput({
  value,
  onChange,
  placeholder,
  idx,
  multiline = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  idx: number;
  multiline?: boolean;
}) {
  const base = cn(
    SETTLE,
    'w-full rounded-2xl bg-white/95 px-6 py-4 text-center font-display text-[14px] font-semibold text-[#1f1f23] placeholder:text-[#1f1f23]/40 focus:bg-white focus:outline-none',
  );
  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={cn(base, 'resize-none')}
        style={settleDelay(idx)}
      />
    );
  }
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={base}
      style={settleDelay(idx)}
    />
  );
}

/** Translate raw API errors into gate-language guidance. There is no "login"
 *  in this product — auth IS the /ggportal link — so a 401 means the session
 *  that link minted has expired (founder 3h, member 15min). Say that.
 *
 *  Start-date lifecycle gates (registration_closed, roster_locked) and the
 *  terms-acceptance gate (terms_not_accepted) all come back as portalApi's
 *  double-JSON-encoded structured error (see api.ts's parseApiError doc
 *  comment) — unwrap those to their human `message` instead of showing the
 *  raw `{"error":"...","message":"..."}` blob. */
export function friendlyGateError(message: string): string {
  if (/login required|401|unauthorized|expired/i.test(message)) {
    return 'Your portal link expired — run /ggportal in Discord and click the fresh link to continue';
  }
  // Every other server error — including a plain fail(status, "human text")
  // string (e.g. the 409 "you already have a pending clan-creation request"
  // guard) — reaches here as apiFetch's raw `"<message> (HTTP <status>)"`.
  // Always show parseApiError's cleaned `.message`, never the raw `message`
  // argument: this used to special-case exactly three structured codes
  // (registration_closed/roster_locked/terms_not_accepted) and fall through
  // to the untouched raw string for everything else, leaking the
  // "(HTTP 409)" suffix (or a raw {"error":...} blob for a JSON-wrapped
  // one) straight into the gate's error copy. parseApiError already
  // resolves those same three codes to the identical `.message`, so
  // returning it unconditionally fixes every other code too, for free.
  return parseApiError(message).message;
}

/** Quiet status line under the pills (errors, hints). */
export function GateNote({ children, tone = 'muted', idx }: { children: ReactNode; tone?: 'muted' | 'error'; idx: number }) {
  return (
    <p
      className={cn(
        SETTLE,
        'px-2 pt-1 text-center font-display text-[11px] uppercase tracking-wider',
        tone === 'error' ? 'text-red-300' : 'text-white/40',
      )}
      style={settleDelay(idx)}
    >
      {children}
    </p>
  );
}
